// src/views/AccommodationProvider/ProviderPropertyManagement.js
import React from 'react';
import { Card, CardBody, CardTitle, Badge, Row, Col, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';

const ProviderPropertyManagement = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Property Management - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `Property Management - ${providerName || 'Your Organization'}`;
    } else {
      return 'Property Management';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const getRegistrationStatusMessage = () => {
    switch (regStatus) {
      case '0':
        return {
          message: 'Please complete your registration to manage properties',
          variant: 'warning'
        };
      case '1':
        return {
          message: 'Registration under review - Property management will be available once approved.',
          variant: 'info'
        };
      case '2':
        return {
          message: 'Registration approved - You can now manage your properties',
          variant: 'success'
        };
      default:
        return {
          message: 'Please complete your registration',
          variant: 'secondary'
        };
    }
  };

  const handleViewProperties = () => {
    // Navigate to view properties page
    navigate('/provider-properties');
  };

  const handleAddProperty = () => {
    // Navigate to add property page
    navigate('/provider-add-property');
  };

  const handleRequestInspection = () => {
    // Navigate to inspection request page
    navigate('/provider-request-inspection');
  };

  const handleBackToDashboard = () => {
    navigate('/provider-dashboard');
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleBackToDashboard}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Dashboard
            </Button>
          </div>
          
          {/* Registration Status Alert */}
          <div className={`alert alert-${getRegistrationStatusMessage().variant} alert-dismissible fade show`} role="alert">
            <i className="bi bi-info-circle me-2"></i>
            {getRegistrationStatusMessage().message}
          </div>
        </div>
      </div>

      {/* Property Management Options */}
      <Row className="mb-4">
        <Col md="4">
          <Card className="mb-3 h-100">
            <CardBody className="d-flex flex-column">
              <CardTitle tag="h5" className="mb-3">
                <i className="bi bi-eye me-2 text-primary"></i>
                View Properties
              </CardTitle>
              <p className="text-muted flex-grow-1">
                View and manage your existing accommodation listings. Check property status, 
                update details, and monitor bookings.
              </p>
              <div className="mt-auto">
                <Button 
                  color="primary" 
                  onClick={handleViewProperties}
                  disabled={regStatus !== '2'}
                  className="w-100"
                >
                  <i className="bi bi-building me-2"></i>
                  View My Properties
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
        
        <Col md="4">
          <Card className="mb-3 h-100">
            <CardBody className="d-flex flex-column">
              <CardTitle tag="h5" className="mb-3">
                <i className="bi bi-plus-circle me-2 text-success"></i>
                Add New Property
              </CardTitle>
              <p className="text-muted flex-grow-1">
                Register a new accommodation property. Complete property details, 
                upload documents, and submit for approval.
              </p>
              <div className="mt-auto">
                <Button 
                  color="success" 
                  onClick={handleAddProperty}
                  disabled={regStatus !== '2'}
                  className="w-100"
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Add New Property
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md="4">
          <Card className="mb-3 h-100">
            <CardBody className="d-flex flex-column">
              <CardTitle tag="h5" className="mb-3">
                <i className="bi bi-clipboard-check me-2 text-warning"></i>
                Request Inspection
              </CardTitle>
              <p className="text-muted flex-grow-1">
                Request property inspections for your registered properties. Schedule 
                inspections and track the approval process.
              </p>
              <div className="mt-auto">
                <Button 
                  color="warning" 
                  onClick={handleRequestInspection}
                  disabled={regStatus !== '2'}
                  className="w-100"
                >
                  <i className="bi bi-calendar-check me-2"></i>
                  Request Inspection
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Quick Stats Row */}
      <Row className="mb-4">
        <Col md="3">
          <Card className="text-center">
            <CardBody>
              <h4 className="text-primary mb-1">0</h4>
              <small className="text-muted">Total Properties</small>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="text-center">
            <CardBody>
              <h4 className="text-success mb-1">0</h4>
              <small className="text-muted">Active Properties</small>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="text-center">
            <CardBody>
              <h4 className="text-warning mb-1">0</h4>
              <small className="text-muted">Under Review</small>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="text-center">
            <CardBody>
              <h4 className="text-info mb-1">0</h4>
              <small className="text-muted">Total Beds</small>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row>
        <Col md="12">
          <Card className="border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-question-circle me-2"></i>
                Need Help?
              </CardTitle>
              <Row>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Property Requirements:</strong>
                  </div>
                  <ul className="small text-muted">
                    <li>Valid ownership or lease documentation</li>
                    <li>Safety and hygiene compliance certificates</li>
                    <li>Insurance coverage verification</li>
                    <li>Property inspection completion</li>
                  </ul>
                </Col>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Registration Process:</strong>
                  </div>
                  <ul className="small text-muted">
                    <li>Complete property details form</li>
                    <li>Upload required documents</li>
                    <li>Schedule property inspection</li>
                    <li>Await approval notification</li>
                  </ul>
                </Col>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Support:</strong>
                  </div>
                  <div className="small text-muted">
                    Contact our support team for assistance with property registration 
                    or any questions about the approval process.
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProviderPropertyManagement;